import clsx from 'clsx';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../../Button';
import { useAppSelector } from '../../../hooks';
import { UserRole } from '../../../services/api.types';
import { ROUTES } from '../../../utils/constants';
import ScheduleSVG from '../../../assets/icons/schedule.svg';
import UserSVG from '../../../assets/icons/user.svg';
import ReportsSVG from '../../../assets/icons/reports.svg';
import BookSVG from '../../../assets/icons/book.svg';
import MoneySVG from '../../../assets/icons/money.svg';

type OptionProps = {
  text: string;
  icon: string;
  alt?: string;
  to: string;
  drawer?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

const Option = ({ text, icon, alt, to, drawer, onClick }: OptionProps) => {
  const location = useLocation();
  const active =
    location.pathname !== '/' ? to.startsWith(location.pathname) : false;

  return (
    <Link
      className={clsx(
        'flex flex-col items-center',
        'group hover:cursor-pointer',
        'no-underline w-full'
      )}
      onClick={onClick}
      to={to}
    >
      {!drawer ? (
        <>
          <Button
            className={clsx(
              'flex justify-center items-center',
              'w-24 h-24 rounded-full',
              'bg-gray-d3 group-hover:bg-orange-primary',
              'transition duration-300',
              { 'bg-orange-primary': active }
            )}
          >
            <img src={icon} alt={alt ?? text} />
          </Button>

          <p className="text-15 my-1 text-blue-primary text-center">{text}</p>
        </>
      ) : (
        <div
          className={clsx(
            'flex flex-row items-center gap-4',
            'w-full h-12 font-bold rounded px-4',
            'group-hover:bg-orange-primary',
            'transition duration-300',
            { 'bg-orange-primary': active }
          )}
        >
          <img src={icon} alt={alt ?? text} />
          <p className="text-15 my-1 text-blue-primary text-center">{text}</p>
        </div>
      )}
    </Link>
  );
};

export type OptionsMenuProps = {
  drawer?: boolean;
  onOptionClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

export const OptionsMenu = ({ drawer, onOptionClick }: OptionsMenuProps) => {
  const user = useAppSelector((state) => state.user);
  if (!user) return null;

  const OptionMenu = (props: OptionProps) => (
    <Option {...props} drawer={drawer} onClick={onOptionClick} />
  );

  if (user.role === UserRole.ADMIN || user.role === UserRole.EMPLOYEE)
    return (
      <>
        <OptionMenu
          to={ROUTES.calendars.index}
          text="Agenda"
          icon={ScheduleSVG}
        />
        <OptionMenu to={ROUTES.users.index} text="Usuários" icon={UserSVG} />
        <OptionMenu
          to={ROUTES.reports.index}
          text="Relatórios"
          icon={ReportsSVG}
        />
        <OptionMenu to={ROUTES.subjects.index} text="Matérias" icon={BookSVG} />
        <OptionMenu
          to={ROUTES.payments.index}
          text="Financeiro"
          icon={MoneySVG}
        />
      </>
    );

  if (user.role === UserRole.TEACHER)
    return (
      <>
        <OptionMenu
          to={ROUTES.calendars.index}
          text="Agenda"
          icon={ScheduleSVG}
        />
        <OptionMenu
          to={ROUTES.reports.index}
          text="Relatórios das Aulas"
          icon={ReportsSVG}
        />
      </>
    );

  if (user.role === UserRole.STUDENT)
    return (
      <OptionMenu
        to={ROUTES.calendars.index}
        text="Agenda"
        icon={ScheduleSVG}
      />
    );

  if (user.role === UserRole.RESPONSIBLE)
    return (
      <>
        <OptionMenu
          to={ROUTES.calendars.index}
          text="Agenda"
          icon={ScheduleSVG}
        />
        <OptionMenu
          to={ROUTES.payments.index}
          text="Financeiro"
          icon={MoneySVG}
        />
      </>
    );

  return <></>;
};
